/**
 * THE authoritative GPU flood-field layer.
 *
 * Every reconstruction of a flood quantity — terrain, bed, depth, water
 * surface, signed visual depth, the wet predicate, water normals — lives here
 * and ONLY here. Production rendering and all 12 debug modes call the same
 * functions, so a debug view can no longer silently drift away from what
 * production actually computes.
 *
 * That drift is not hypothetical. Before this module existed the audit found
 * 8 independent AOI/index transforms, 2 different water-surface expressions,
 * 4 copies of the wet predicate and 3 mutually different normal
 * reconstructions; and a real production bug was traced to `maskNode` and the
 * debug `emissiveNode` chain reading the same field through separately-written
 * expressions, one of which never executed.
 *
 * ---------------------------------------------------------------------------
 * AUTHORITATIVE COORDINATE CONVENTION (one mapping, documented once)
 * ---------------------------------------------------------------------------
 * Grid storage : row-major, `index = row * gridSize + col`.
 *                row 0 = south edge, col 0 = west edge.
 * Texel        : `data[y * gridSize + x]` lands on texel (x, y), i.e.
 *                x = col (east), y = row (north). Guaranteed by DataTexture's
 *                `flipY = false` default (DataTexture.js:71) — canonical
 *                fields must never enable flipY.
 * UV           : u = x / (gridSize - 1), v = y / (gridSize - 1); origin (0,0)
 *                at the south-west cell CENTRE, (1,1) at the north-east cell
 *                centre. This is the mesh's own `uv` attribute convention
 *                (see buildGridGeometry in the views) and matches texel
 *                centres, not cell corners.
 * World        : +x east, +z south-to-north as laid out by the geometry, y up.
 * Bounds       : all canonical fields use ClampToEdgeWrapping, and exact cell
 *                access clamps explicitly via `clampCell` — the two agree by
 *                construction so an edge texel is never wrapped or duplicated
 *                differently between the physics and render paths.
 *
 * ---------------------------------------------------------------------------
 * EXACT vs INTERPOLATED ACCESS — the distinction is load-bearing
 * ---------------------------------------------------------------------------
 *   loadBedCell / loadDepthCell / loadTerrainCell (ivec2 cell)
 *       Exact integer texel. No interpolation, no mip, level 0. For the
 *       NUMERICAL STENCIL. Using an interpolated read here would corrupt the
 *       solver while still looking plausible on screen.
 *
 *   sampleBed / sampleDepth / sampleTerrain (uv)
 *       Continuous field reconstruction, level 0 only, deterministic. For
 *       RENDERING. Never use inside the physics kernel.
 *
 * Which interpolation mechanism `sample*` uses is deliberately invisible to
 * callers — see `sampleScalar`.
 */
import * as THREE from 'three/webgpu'
import {
  Fn, int, ivec2, float, vec2, vec3, select, smoothstep, uniform,
  max as tslMax, min as tslMin, texture, textureLoad, transformNormalToView,
} from 'three/tsl'
import type { Node } from 'three/webgpu'
import type { ShaderNodeObject } from 'three/tsl'

/**
 * A chainable TSL node. `Node` alone is the bare base class and carries none
 * of the `.add`/`.mul`/`.mod` operators — those live on the proxied
 * `ShaderNodeObject`, which is what every TSL builder actually returns.
 */
export type TslNode = ShaderNodeObject<Node>

/**
 * A scalar TSL uniform node. Typed via `ReturnType<typeof uniform>` rather
 * than `unknown` so arithmetic against it stays compiler-checked — three's TSL
 * types are complete enough that this module needs no `@ts-nocheck`.
 */
export type ScalarUniform = ReturnType<typeof uniform>

/**
 * Metres either side of the signedVisualDepth=0 shoreline that edge coverage
 * ramps across, for alphaToCoverage's MSAA antialiasing ONLY. `isWet` is the
 * actual keep/discard decision and does not reference this band — screen-space
 * antialiasing must never become the wet predicate.
 */
export const EDGE_ANTIALIAS_BAND_M = 0.01

/** UV step used for finite-difference normals — independent of sim grid spacing. */
const NORMAL_EPS_UV = 1 / 512

/**
 * The AOI-uv -> texture-uv affine remap, as plain numbers.
 *
 * Exported and expressed once so the shader path and its unit test share a
 * single definition — the half-cell error this corrects is invisible by
 * inspection and was only caught by measuring, so it needs a guard that
 * cannot drift from the implementation. See `aoiUvToTexUv` for the derivation.
 */
export function aoiUvRemap(gridSize: number): { scale: number; offset: number } {
  return { scale: (gridSize - 1) / gridSize, offset: 0.5 / gridSize }
}

/**
 * Texel coordinate hardware bilinear will evaluate at, for a given texture uv.
 * Texel centres sit at integers. Exposed for tests; the GPU applies this
 * itself.
 */
export function hardwareTexelCoord(texUv: number, gridSize: number): number {
  return texUv * gridSize - 0.5
}

// ---------------------------------------------------------------------------
// EXACT access — standalone, registry-free
// ---------------------------------------------------------------------------
// Deliberately module-level functions rather than factory members. The physics
// kernel needs exactly these, and nothing else the factory provides: it reads
// specific source textures chosen per ping-pong direction, so it must NOT be
// handed the factory's *current-depth* registry, which is a rendering concept.
// Keeping them here lets the solver share one definition of the index and
// clamp conventions with the renderer without owning any visual state.

/** Linear compute index -> cell coordinate. THE index convention (see header). */
export function cellOfIndex(index: TslNode, gridSize: number) {
  return ivec2(int(index.mod(gridSize)), int(index.div(gridSize)))
}

/** Clamp-to-edge for exact cell access, matching the textures' ClampToEdgeWrapping. */
export function clampCellComponent(component: TslNode, gridSize: number) {
  return tslMax(int(0), tslMin(component, int(gridSize - 1)))
}

/**
 * THE exact-texel read: `textureLoad` bypasses the sampler entirely, so it is
 * unaffected by the filter settings the render path relies on, and it never
 * interpolates. Using an interpolated read in a numerical stencil would
 * corrupt the solver while still looking plausible on screen.
 */
export function loadCell(tex: THREE.Texture, cell: TslNode) {
  return textureLoad(tex, cell).r
}

/**
 * Creates a canonical scalar field texture from CPU data.
 *
 * One definition so every canonical field gets identical settings. The mip and
 * filter policy is load-bearing: `generateMipmaps = false` means a single
 * level, so mip selection cannot vary with the camera and the physical field
 * is camera-independent by construction. `DataTexture` also defaults
 * `flipY = false`, which is what makes `data[y * gridSize + x]` land on texel
 * (x, y) — the same convention the solver indexes with.
 */
export function createScalarField(data: Float32Array, gridSize: number, name: string): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, gridSize, gridSize, THREE.RedFormat, THREE.FloatType)
  tex.name = name
  tex.magFilter = THREE.LinearFilter
  tex.minFilter = THREE.LinearFilter
  tex.generateMipmaps = false
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.needsUpdate = true
  return tex
}

/**
 * The GPU-resident counterpart of a `FloodDomain`.
 *
 * Kept as a distinct type, and built here rather than in flood-domain.ts, so
 * that module stays free of Three.js and remains importable from a Worker.
 * GPU textures must never travel back into `FloodDomain`.
 */
export interface FloodGpuDomain {
  terrainTexture: THREE.DataTexture
  bedTexture: THREE.DataTexture
  /** Disposes both textures. The creator owns them; solver and renderer only reference them. */
  dispose(): void
}

/**
 * Uploads a domain's static fields to the GPU.
 *
 * NOTE on lifetime: `THREE.DataTexture` keeps `image.data` referencing the
 * ORIGINAL Float32Array — it does not copy on upload. So these textures and
 * `domain.terrain`/`domain.bed` share backing buffers for as long as the
 * textures live. That is fine here (the domain is immutable by contract), but
 * it is exactly why a future Worker migration must not transfer/detach those
 * ArrayBuffers while textures still reference them. Nothing is detached here.
 */
export function createFloodGpuDomain(domain: {
  width: number
  terrain: Float32Array
  bed: Float32Array
}): FloodGpuDomain {
  const terrainTexture = createScalarField(domain.terrain, domain.width, 'TerrainField')
  const bedTexture = createScalarField(domain.bed, domain.width, 'BedField')
  return {
    terrainTexture,
    bedTexture,
    dispose() {
      terrainTexture.dispose()
      bedTexture.dispose()
    },
  }
}

export interface FloodFieldResources {
  /** Static hydraulic floor: conditioned terrain + buildings stamped. R32Float. */
  bedTexture: THREE.Texture
  /** Static visible ground, no buildings. R32Float. NEVER interchangeable with bed. */
  terrainTexture: THREE.Texture
  /** Initial binding for the CURRENT depth texture. Swapped via `rebindDepth`. */
  depthTexture: THREE.Texture
  gridSize: number
  /** Uniform: metres per cell. */
  cellSizeUniform: ScalarUniform
  /** Uniform: metres the RENDERED surface sits below bed at zero depth. */
  visualDryOffsetUniform: ScalarUniform
  /** Uniform: metres a CONFIRMED-wet fragment is nudged above terrain. */
  visualSurfaceBiasUniform: ScalarUniform
}

/**
 * Builds the flood-field accessors for one solver instance.
 *
 * A FACTORY rather than free functions, for one concrete reason: depth
 * sampling has to survive ping-pong. Every render-side depth read must be a
 * registered `TextureNode` whose `.value` is repointed when A/B swaps
 * (`NodeSampledTexture.update()` refreshes the binding without any material or
 * node-graph rebuild). Free functions would either capture a texture
 * permanently — freezing the render to A and showing stale water every other
 * step — or force every caller to thread a node registry around by hand,
 * which is exactly the kind of per-caller bookkeeping that produced the
 * divergence this module exists to remove. The factory owns the registry.
 */
export function createFloodFields(res: FloodFieldResources) {
  const {
    bedTexture, terrainTexture, gridSize,
    cellSizeUniform, visualDryOffsetUniform, visualSurfaceBiasUniform,
  } = res

  /** Every registered current-depth sample. Repointed together by `rebindDepth`. */
  const depthNodes: any[] = []
  let boundDepthTexture: THREE.Texture = res.depthTexture

  // ---------------------------------------------------------------------
  // Coordinates
  // ---------------------------------------------------------------------

  // Delegate to the shared module-level definitions so the renderer and the
  // solver provably use ONE index/clamp convention.
  const cellOf = (index: TslNode) => cellOfIndex(index, gridSize)
  const clampCell = (component: TslNode) => clampCellComponent(component, gridSize)

  /** Offsets a uv by a delta. Kept here so the offset convention has one definition. */
  const offsetUv = (uvNode: any, du: number, dv: number) =>
    vec2(uvNode.x.add(du), uvNode.y.add(dv))

  /**
   * AOI uv -> TEXTURE uv. Corrects a real half-cell registration error, and
   * the difference is NOT negligible.
   *
   * A DEM sample `i` sits at normalized AOI position `i / (gridSize - 1)`:
   * that is how the terrain was sampled (aoi-terrain-extractor steps
   * `row / (fineGridSize - 1)`) and how the mesh is laid out
   * (`x = (col / (gridSize - 1) - 0.5) * widthM`). So the value at AOI
   * position `u` is the DEM field evaluated at fractional index
   * `u * (gridSize - 1)`.
   *
   * Hardware bilinear does NOT use that mapping. It places texel centres at
   * `(i + 0.5) / gridSize`, so a raw uv fetch evaluates at
   * `u * gridSize - 0.5` — off by `(0.5 - u)` texels, i.e. up to HALF A CELL,
   * and in opposite directions at the two edges.
   *
   * Measured on a 500 m AOI at 256² (~2 m cells) against the CPU
   * reconstruction this replaces: raw uv gives max 1.08 m / mean 0.30 m
   * elevation error; this remap gives max 2.8e-14 m. Not precision noise —
   * a systematic spatial shift.
   *
   * Inverse of the hardware mapping: to make hardware bilinear land on
   * texel coordinate `u * (gridSize - 1)`, feed it
   * `(u * (gridSize - 1) + 0.5) / gridSize`.
   */
  const { scale: aoiUvScale, offset: aoiUvOffset } = aoiUvRemap(gridSize)
  const aoiUvToTexUv = (uvNode: any) => uvNode.mul(aoiUvScale).add(aoiUvOffset)

  // ---------------------------------------------------------------------
  // Exact (physics) access
  // ---------------------------------------------------------------------

  /**
   * THE exact-texel read. `textureLoad` bypasses the sampler entirely, so it
   * is unaffected by the filter settings the render path relies on.
   *
   * Note the depth variant takes its texture explicitly: the physics kernels
   * are built once per ping-pong direction against a SPECIFIC source texture,
   * which is not the same thing as "the current one".
   */
  const loadBedCell = (cell: TslNode) => loadCell(bedTexture, cell)
  const loadTerrainCell = (cell: TslNode) => loadCell(terrainTexture, cell)
  const loadDepthCell = (depthTex: THREE.Texture, cell: TslNode) => loadCell(depthTex, cell)

  // ---------------------------------------------------------------------
  // Interpolated (render) access
  // ---------------------------------------------------------------------

  /**
   * THE interpolated read — the single place the interpolation MECHANISM is
   * chosen, so callers never learn which one is active.
   *
   * Currently hardware bilinear: Phase 1A confirmed `float32-filterable` is
   * available on the target adapter, and canonical fields carry LinearFilter
   * with no mip chain, so this resolves to a deterministic level-0 bilinear
   * fetch. If an adapter without that feature has to be supported, the manual
   * 4-tap `textureLoad` reconstruction goes HERE and nothing else in the
   * codebase changes — that is the whole point of routing every sample
   * through one function.
   */
  const sampleScalar = (tex: THREE.Texture, uvNode: any) => texture(tex, uvNode).r

  /**
   * ONE registration for all three canonical fields.
   *
   * Bed and depth used to bypass `aoiUvToTexUv` and sample at raw uv. That was
   * a real, measured defect, and it was NOT a uniform translation: the offset
   * is `(u - 0.5)` cells, so the bed/depth field was scaled by `(N-1)/N` about
   * the AOI centre — exact at the middle, half a cell wrong at every edge
   * (0.98 m at 256² on a 500 m AOI).
   *
   * Why that mattered: the water surface is `bed + depth - offset` while the
   * ground mesh renders `terrain`. Reading the two ~1 m apart horizontally
   * turns into a VERTICAL disagreement of `1 m x local slope` — about 0.3 m on
   * a 30% slope, an order of magnitude larger than both `visualDryOffsetM`
   * (0.02 m) and `visualSurfaceBiasM` (0.03 m). That is enough for water to
   * render above or sunk into the ground it is supposed to be sitting on, with
   * a distinctive signature: worst at the AOI boundary, exactly zero at the
   * centre, invisible on flat terrain.
   *
   * PHYSICS IS UNAFFECTED. The solver reads through `loadCell`/`textureLoad`
   * at integer cells, which never touches this path, so the frozen numerical
   * baseline is untouched by construction. What changes is rendered output
   * near the domain edges — deliberately, that being the point.
   */
  const sampleBed = (uvNode: any) => sampleScalar(bedTexture, aoiUvToTexUv(uvNode))

  /**
   * Visible ground elevation at an AOI uv. Applies `aoiUvToTexUv`, so this
   * reproduces the CPU bilinear reconstruction the ground mesh used before
   * GPU displacement — exactly, not approximately.
   */
  const sampleTerrain = (uvNode: any) => sampleScalar(terrainTexture, aoiUvToTexUv(uvNode))

  /**
   * THE current-depth sample. Registers the node so `rebindDepth` can repoint
   * it on every ping-pong swap — a raw `texture(depthTextureA, uv)` written
   * anywhere else would be frozen to A forever.
   *
   * Same registration as bed and terrain (see `sampleBed`). Depth and bed in
   * particular MUST share one: `signedVisualDepth` is their difference, so
   * sampling them at different positions would compare a water surface against
   * a floor taken from somewhere else.
   */
  const sampleDepth = (uvNode: any) => {
    const node = texture(boundDepthTexture, aoiUvToTexUv(uvNode))
    depthNodes.push(node)
    return node.r
  }

  /** Repoints every registered depth sample at the new current texture. No rebuild, no copy. */
  const rebindDepth = (tex: THREE.Texture) => {
    boundDepthTexture = tex
    for (const node of depthNodes) node.value = tex
  }

  // ---------------------------------------------------------------------
  // Derived surfaces — physical vs visual kept strictly separate
  // ---------------------------------------------------------------------

  /**
   * THE flood-surface reconstruction. ONE call samples bed and depth exactly
   * once at `uvNode` and returns every derived quantity computed from those
   * same two reads.
   *
   * Deliberately a bundle rather than separate `visualSurfaceHeight(uv)` /
   * `signedVisualDepth(uv)` entry points. Independent functions would each
   * re-sample, so a material asking for two of them would emit two bed reads
   * and two depth reads for one fragment — the audit already found the
   * water-surface formula being evaluated five times per fragment, and
   * per-function sampling would bake that in permanently. It also removes any
   * way for two callers to disagree: they cannot pick different subsets of a
   * shared computation because there is only one computation.
   *
   * Field meanings, physical strictly separated from visual:
   *
   *   bed      hydraulic floor (conditioned terrain + buildings)
   *   depth    physical water depth, metres
   *   physical PHYSICAL free surface = bed + depth. No visual offsets. The
   *            base is the BED, not visible terrain: at a building footprint
   *            the water floor genuinely IS the roof, which is what the
   *            solver integrates against.
   *   visual   rendered surface = physical - visualDryOffsetM, so a zero-depth
   *            cell renders just BELOW its floor and wet/dry becomes a
   *            continuous signed quantity (this project's analogue of
   *            skunami's minWaterHeight = -0.05).
   *   signed   negative = dry, zero = shoreline, positive = wet.
   *   biased   what actually gets rendered: visual + the wet-only z-fight bias.
   *
   * TWO fp32 details that must not be "simplified":
   *
   *  1. `visual` is `(bed + depth) - offset`, NOT `bed + (depth - offset)`.
   *     Equal in real arithmetic, NOT bit-identical in fp32. This association
   *     is the one the frozen, visually validated output came from.
   *
   *  2. `signed` is `visual - bed`, NOT the algebraically identical
   *     `depth - offset`. The redundant-looking subtraction is intentional
   *     for the same reason — collapsing it would move the wet/dry boundary
   *     by an ulp across the whole domain.
   *
   * The reference floor for `signed` is the BED, deliberately NOT
   * `terrainTexture`. At a building footprint the bed sits at roof height
   * while terrain sits at ground level, so measuring the surface against
   * terrain there would read as permanently, deeply flooded. Using one field
   * for both the surface and its own floor is what keeps this correct over
   * buildings.
   */
  const fieldsAt = (uvNode: any) => {
    // Both reads happen unconditionally, before anything branches — see the
    // TSL control-flow hazard note on `isWet`.
    const bed = sampleBed(uvNode)
    const depth = sampleDepth(uvNode)
    const physical = bed.add(depth)
    const visual = physical.sub(visualDryOffsetUniform)
    const signed = visual.sub(bed)
    const biased = visual.add(select(signed.greaterThan(0), visualSurfaceBiasUniform, float(0)))
    return { bed, depth, physical, visual, signed, biased }
  }

  /**
   * THE wet predicate — the VISUAL wet/dry decision, camera-independent by
   * construction because it is a pure function of the signed-depth field.
   *
   * This is deliberately NOT the same concept as the solver's physical wet
   * state (`depth > 0`): this one carries `visualDryOffsetM`, so a cell with
   * a hair of physical water still reads dry on screen. The solver's notion
   * lives in the kernel and is not reconstructed here.
   *
   * Callers pass the signed value they already computed, so the field is
   * sampled once and the predicate never re-samples behind their back.
   */
  const isWet = (signed: any) => signed.greaterThan(0)

  /**
   * Edge coverage for MSAA antialiasing ONLY — a smooth ramp across a FIXED
   * world-space band centred on the same zero-crossing `isWet` uses. It never
   * decides visibility; it only smooths how already-kept fragments rasterize.
   *
   * Fixed-width and world-space on purpose: no fwidth/dFdx/dFdy anywhere, so
   * this is camera-invariant by construction rather than by convention.
   */
  const edgeCoverage = (signed: any) =>
    smoothstep(-EDGE_ANTIALIAS_BAND_M, EDGE_ANTIALIAS_BAND_M, signed)

  /**
   * THE production water normal: central differences of the SAME biased
   * surface `positionNode` renders, so normals and geometry cannot disagree.
   *
   * UV spans [0,1] across a domain roughly `cellSizeM * gridSize` metres wide,
   * so a UV step of NORMAL_EPS_UV is about `EPS * cellSizeM * gridSize` metres
   * — needed to turn a height difference into a real slope. On a non-square
   * AOI `cellSizeM` is an average of the two axes, so the world step is
   * approximate; that is pre-existing and unchanged here.
   */
  const waterNormal = (uvNode: any) => {
    const worldStep = cellSizeUniform.mul(gridSize).mul(2 * NORMAL_EPS_UV)
    return Fn(() => {
      // Four taps of the SAME reconstruction position uses, so the normal can
      // never describe a different surface than the one being shaded.
      const surfR = fieldsAt(offsetUv(uvNode, NORMAL_EPS_UV, 0)).biased
      const surfL = fieldsAt(offsetUv(uvNode, -NORMAL_EPS_UV, 0)).biased
      const surfU = fieldsAt(offsetUv(uvNode, 0, NORMAL_EPS_UV)).biased
      const surfD = fieldsAt(offsetUv(uvNode, 0, -NORMAL_EPS_UV)).biased
      const dX = surfR.sub(surfL).div(worldStep)
      const dZ = surfU.sub(surfD).div(worldStep)
      return transformNormalToView(vec3(dX.negate(), 1.0, dZ.negate())).toVertexStage()
    })()
  }

  /**
   * ONE terrain gradient definition, shared by the ground normal and the
   * slope-based ground colour so they cannot disagree about spacing, offsets
   * or units. Returns d(height)/d(world metres) along each axis.
   */
  const terrainGradient = (uvNode: any) => {
    const worldStep = cellSizeUniform.mul(gridSize).mul(2 * NORMAL_EPS_UV)
    const hR = sampleTerrain(offsetUv(uvNode, NORMAL_EPS_UV, 0))
    const hL = sampleTerrain(offsetUv(uvNode, -NORMAL_EPS_UV, 0))
    const hU = sampleTerrain(offsetUv(uvNode, 0, NORMAL_EPS_UV))
    const hD = sampleTerrain(offsetUv(uvNode, 0, -NORMAL_EPS_UV))
    return { dX: hR.sub(hL).div(worldStep), dZ: hU.sub(hD).div(worldStep) }
  }

  /**
   * Ground normal for the GPU-displaced terrain. A SEPARATE quantity from
   * `waterNormal` — different surface, different field — and deliberately not
   * merged with it. Needed because vertex normals baked from CPU-displaced
   * geometry become meaningless once displacement moves into positionNode:
   * the geometry on the CPU is a flat grid.
   */
  const terrainNormal = (uvNode: any) => Fn(() => {
    const g = terrainGradient(uvNode)
    return transformNormalToView(vec3(g.dX.negate(), 1.0, g.dZ.negate())).toVertexStage()
  })()

  /**
   * Scalar terrain steepness, matching the quantity the previous CPU
   * `buildTerrainColors` derived: the mean of the two per-axis absolute
   * gradients. Consumers map it to a palette; the palette is a view concern
   * and stays out of this module.
   */
  const terrainSlope = (uvNode: any) => {
    const g = terrainGradient(uvNode)
    return g.dX.abs().add(g.dZ.abs()).mul(0.5)
  }

  return {
    // coordinates
    cellOf, clampCell, offsetUv, aoiUvToTexUv,
    // terrain reconstruction (ground rendering)
    terrainNormal, terrainSlope,
    // exact / physics access
    loadBedCell, loadTerrainCell, loadDepthCell,
    // interpolated / render access
    sampleBed, sampleTerrain, sampleDepth,
    // THE reconstruction + pure functions over its output
    fieldsAt, isWet, edgeCoverage, waterNormal,
    // ping-pong
    rebindDepth,
  }
}

export type FloodFields = ReturnType<typeof createFloodFields>

/**
 * The terrain-reconstruction subset. Ground rendering needs exactly these
 * three; typing against this rather than the whole `FloodFields` means a
 * ground material cannot reach depth sampling, the ping-pong registry or the
 * water surface even by accident.
 */
export type TerrainFields = Pick<FloodFields, 'sampleTerrain' | 'terrainNormal' | 'terrainSlope'>
